import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { PlayerRef } from '@remotion/player';
import { CompositeSegment } from '../../types/Segment/CompositeSegment';
import { SegmentType } from '../../types/Segment/Type';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updatePlayerStoreWithId } from '@/stores/playerV2';
import {
  getAbsolutePlayerTime,
  getPreviousSegmentAbsoluteTime,
  getSegmentAtCurrentFrame
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';

export const useUpdateCurrentSrtIndex = ({ playerRef }: { playerRef: RefObject<PlayerRef> }) => {
  const transcriptStore = useTranscriptContext();
  const { clipId, clipData, fps } = useClipsContext();
  const startTimesAtSrtIndex = useMemo(() => {
    if (transcriptStore.timeSrtIndexArray.length === 0) {
      return [];
    }
    return transcriptStore.timeSrtIndexArray.map(a => a.startTime);
  }, [transcriptStore.timeSrtIndexArray]);

  const getCompositeSegmentAbsoluteTime = useCallback(
    (currentTime: number) => {
      const segmentAtCurrentFrame = getSegmentAtCurrentFrame(currentTime, clipData);

      if (!segmentAtCurrentFrame) {
        return null;
      }

      const isCurrentSegmentComposite = segmentAtCurrentFrame.type === SegmentType.COMPOSITE;

      if (!isCurrentSegmentComposite) {
        return null;
      }
      const compositeSegment: CompositeSegment = segmentAtCurrentFrame;

      const compositeSegmentAbsoluteTimeWithTrimOffset =
        getAbsolutePlayerTime(currentTime, clipData, -Infinity) -
        getPreviousSegmentAbsoluteTime(compositeSegment, clipData, -Infinity) +
        compositeSegment.currentBounds.fromFrame / fps;

      return compositeSegmentAbsoluteTimeWithTrimOffset;
    },
    [clipData, fps]
  );

  /**
   * Update the player srt index based on time and the srt index of the words
   */
  useEffect(() => {
    if (startTimesAtSrtIndex.length === 0) {
      return;
    }

    const updatePlayerStore = (compositeSegmentAbsoluteTimeWithTrimOffset: number) => {
      if (compositeSegmentAbsoluteTimeWithTrimOffset >= 0) {
        const index = startTimesAtSrtIndex.findLastIndex(time => time <= compositeSegmentAbsoluteTimeWithTrimOffset);

        updatePlayerStoreWithId(clipId, {
          currentSrtIndex: transcriptStore.timeSrtIndexArray[index]?.srtIndex,
          currentTime: compositeSegmentAbsoluteTimeWithTrimOffset,
          paused: false,
          isSegmentActive: true
        });
      }
    };

    const onPlayerTimeUpdateFunction = (currentTime: number) => {
      const compositeSegmentAbsoluteTimeWithTrimOffset = getCompositeSegmentAbsoluteTime(currentTime);
      if (compositeSegmentAbsoluteTimeWithTrimOffset === null) {
        updatePlayerStoreWithId(clipId, {
          isSegmentActive: false
        });
        return;
      }
      updatePlayerStore(compositeSegmentAbsoluteTimeWithTrimOffset);
    };

    onPlayerTimeUpdateFunction(remotionPlayerStateStore.getSnapshot().currentTime);
    const unsubscribe = remotionPlayerStateStore.subscribe(() => {
      onPlayerTimeUpdateFunction(remotionPlayerStateStore.getSnapshot().currentTime);
    });
    return () => {
      unsubscribe();
    };
  }, [clipId, startTimesAtSrtIndex, transcriptStore.timeSrtIndexArray, getCompositeSegmentAbsoluteTime]);

  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      const onPlay = () => {
        updatePlayerStoreWithId(clipId, {
          paused: true
        });
      };
      const onPause = () => {
        updatePlayerStoreWithId(clipId, {
          paused: false
        });
      };
      player.addEventListener('play', onPlay);
      player.addEventListener('pause', onPause);

      return () => {
        player.removeEventListener('play', onPlay);
        player.removeEventListener('pause', onPause);
      };
    }
  }, [clipId, playerRef]);
};
